// Cache da "Avaliação da IA" pós-prova — chaveada por provaId.
// Persistido em localStorage + opcionalmente no server (via /api/student/ai-analysis).
//
// Flow:
//   - Result gera → write localStorage + write server (async, fire-and-forget)
//   - Revisao abre → read server (se bankId disponível) + fallback localStorage
//
// O contrato AiAnalysisCache permanece o mesmo; a sync com server é opt-in
// via fetchFromServer/saveToServer chamados explicitamente pelos consumidores.

export interface AiAnalysisCache {
  text: string;
  generatedAt: string;
}

export function aiAnalysisStorageKey(provaId: string): string {
  return `ai-analysis-prova-${provaId}`;
}

export function readAiAnalysisCache(provaId: string): AiAnalysisCache | null {
  try {
    const raw = localStorage.getItem(aiAnalysisStorageKey(provaId));
    if (!raw) return null;
    return JSON.parse(raw) as AiAnalysisCache;
  } catch {
    return null;
  }
}

export function writeAiAnalysisCache(
  provaId: string,
  cache: AiAnalysisCache
): void {
  try {
    localStorage.setItem(aiAnalysisStorageKey(provaId), JSON.stringify(cache));
  } catch {
    // ignore quota / private mode
  }
}

export function deleteAiAnalysisCache(provaId: string): void {
  try {
    localStorage.removeItem(aiAnalysisStorageKey(provaId));
  } catch {
    // ignore
  }
}

// ============================================================================
// Sync com server — opt-in. Falha silenciosa: se rede cai, localStorage
// continua sendo a fonte. Os chamadores podem ignorar erros sem prejuízo
// imediato pro fluxo do aluno.
// ============================================================================

export async function fetchAiAnalysisFromServer(
  bankId: string,
  provaId: string
): Promise<AiAnalysisCache | null> {
  try {
    const url = `/api/student/ai-analysis?bankId=${encodeURIComponent(
      bankId
    )}&provaId=${encodeURIComponent(provaId)}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      evaluation: { text: string; generatedAt: string } | null;
    };
    if (!data.evaluation) return null;
    return {
      text: data.evaluation.text,
      generatedAt: data.evaluation.generatedAt
    };
  } catch {
    return null;
  }
}

export async function saveAiAnalysisToServer(
  bankId: string,
  provaId: string,
  cache: AiAnalysisCache,
  model?: string | null
): Promise<void> {
  try {
    await fetch("/api/student/ai-analysis", {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ bankId, provaId, text: cache.text, model })
    });
  } catch {
    // ignore — localStorage já tem o cache
  }
}

export async function deleteAiAnalysisFromServer(
  bankId: string,
  provaId: string
): Promise<void> {
  try {
    await fetch(
      `/api/student/ai-analysis?bankId=${encodeURIComponent(
        bankId
      )}&provaId=${encodeURIComponent(provaId)}`,
      { method: "DELETE", credentials: "include" }
    );
  } catch {
    // ignore
  }
}
